import { Form } from 'factorial-form'
import { AttendanceGridRoundingBiasEnum } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'

import { convertToSecondsOrNull } from 'modules/core/lib/formatTime'
import hasFeature from 'modules/core/lib/hasFeature'

import type { ToleranceSettingFragment } from 'modules/attendance/components/TimeTrackingSettings/ToleranceSettings/hooks/useToleranceSettings'

export type ToleranceType = 'all_shifts' | 'boundary_shifts'
export type GridBias = 'none' | 'company' | 'balanced' | 'neutral'

class ToleranceForm extends Form {
  constructor(values: object) {
    super(values, {
      id: 'number',
      toleranceType: 'string',
      shiftsToleranceEnabled: 'boolean',
      clockInRoundingEnabled: 'boolean',
      clockInBefore: 'number',
      clockInAfter: 'number',
      clockOutRoundingEnabled: 'boolean',
      clockOutBefore: 'number',
      clockOutAfter: 'number',
      workedHoursToleranceEnabled: 'boolean',
      workedHoursRoundingEnabled: 'boolean',
      belowEstimated: 'number',
      aboveEstimated: 'number',
      attendanceEmployeesSettingId: 'number',
      gridRoundingBias: 'string',
      clockInRoundUpEnabled: 'boolean',
    })

    makeObservable(this, {
      shiftsIsDirty: computed,
      shiftsIsNull: computed,
      shiftsRoundingWithoutRange: computed,
      clockInRoundingWithoutRange: computed,
      clockOutRoundingWithoutRange: computed,
      workedHoursIsDirty: computed,
      workedHoursIsNull: computed,
      workedHoursRoundingWithoutRange: computed,
    })
  }

  get shiftsIsDirty(): boolean {
    return (
      (this.get('toleranceType').isDirty ||
        this.get('gridRoundingBias').isDirty ||
        this.get('clockInRoundingEnabled').isDirty ||
        this.get('clockInBefore').isDirty ||
        this.get('clockInAfter').isDirty ||
        this.get('clockOutRoundingEnabled').isDirty ||
        this.get('clockOutAfter').isDirty ||
        this.get('clockInRoundUpEnabled').isDirty ||
        this.get('clockOutAfter').isDirty) &&
      !this.shiftsIsNull &&
      !this.shiftsRoundingWithoutRange
    )
  }

  get shiftsIsNull(): boolean {
    return (
      this.get('shiftsToleranceEnabled').value &&
      !this.get('clockInBefore').value &&
      !this.get('clockInAfter').value &&
      !this.get('clockInRoundingEnabled').value &&
      !this.get('clockOutBefore').value &&
      !this.get('clockOutAfter').value &&
      !this.get('clockOutRoundingEnabled').value
    )
  }

  get shiftsRoundingWithoutRange(): boolean {
    return this.clockInRoundingWithoutRange || this.clockOutRoundingWithoutRange
  }

  get clockInRoundingWithoutRange(): boolean {
    return (
      !this.get('clockInBefore').value &&
      !this.get('clockInAfter').value &&
      this.get('clockInRoundingEnabled').value
    )
  }

  get clockOutRoundingWithoutRange(): boolean {
    return (
      !this.get('clockOutBefore').value &&
      !this.get('clockOutAfter').value &&
      this.get('clockOutRoundingEnabled').value
    )
  }

  get workedHoursIsDirty(): boolean {
    return (
      (this.get('belowEstimated').isDirty ||
        this.get('aboveEstimated').isDirty ||
        this.get('workedHoursRoundingEnabled').isDirty) &&
      !this.workedHoursIsNull &&
      !this.workedHoursRoundingWithoutRange
    )
  }

  get workedHoursIsNull(): boolean {
    return (
      this.get('workedHoursToleranceEnabled').value &&
      !this.get('belowEstimated').value &&
      !this.get('aboveEstimated').value &&
      !this.get('workedHoursRoundingEnabled').isDirty
    )
  }

  get workedHoursRoundingWithoutRange(): boolean {
    return (
      !this.get('belowEstimated').value &&
      !this.get('aboveEstimated').value &&
      this.get('workedHoursRoundingEnabled').isDirty
    )
  }

  get isNewRecord(): boolean {
    return +this.get('id').value === 0
  }

  get gridRoundingEnabled(): boolean {
    const gridRoundingAllowed = hasFeature('dev_time_tracking_grid_rounding')
    if (!gridRoundingAllowed) return false
    if (this.gridRoundingBias === null) return false

    const enabledTypes: GridBias[] = ['company', 'balanced', 'neutral']
    return enabledTypes.includes(this.gridRoundingBias)
  }

  get gridRoundingBias(): AttendanceGridRoundingBiasEnum | null {
    const validTypes: GridBias[] = ['company', 'balanced', 'neutral']
    const value = this.data().gridRoundingBias

    if (!validTypes.includes(value)) return null

    return value
  }

  getBooleanValue = (value: string | boolean): boolean => {
    return typeof value === 'string' ? false : value
  }

  parsedData = (): ToleranceSettingFragment => {
    const formData = this.data()
    const clockOutRoundingEnabled = this.gridRoundingEnabled
      ? !!(
          convertToSecondsOrNull(formData.clockOutBefore) ||
          convertToSecondsOrNull(formData.clockOutAfter)
        )
      : this.getBooleanValue(formData.clockOutRoundingEnabled)

    const clockInRoundingEnabled = this.gridRoundingEnabled
      ? !!(
          convertToSecondsOrNull(formData.clockInBefore) ||
          convertToSecondsOrNull(formData.clockInAfter)
        )
      : this.getBooleanValue(formData.clockInRoundingEnabled)
    return {
      id: formData.id,
      clockInRoundingEnabled,
      clockOutRoundingEnabled,
      attendanceEmployeesSettingId: formData.attendanceEmployeesSettingId,
      toleranceType: formData.toleranceType || 'all_shifts',
      shiftsToleranceEnabled: this.getBooleanValue(formData.shiftsToleranceEnabled),
      clockInAfter: convertToSecondsOrNull(formData.clockInAfter),
      clockInBefore: convertToSecondsOrNull(formData.clockInBefore),
      clockOutAfter: convertToSecondsOrNull(formData.clockOutAfter),
      clockOutBefore: convertToSecondsOrNull(formData.clockOutBefore),
      workedHoursToleranceEnabled: this.getBooleanValue(formData.workedHoursToleranceEnabled),
      workedHoursRoundingEnabled: this.getBooleanValue(formData.workedHoursRoundingEnabled),
      belowEstimated: convertToSecondsOrNull(formData.belowEstimated),
      aboveEstimated: convertToSecondsOrNull(formData.aboveEstimated),
      gridRounding: this.gridRoundingEnabled,
      gridRoundingBias: this.gridRoundingBias,
      clockInRoundUpEnabled: this.getBooleanValue(formData.clockInRoundUpEnabled),
    }
  }
}

export default ToleranceForm
